import { labelWithSpecies } from 'common/utils/species'
import { yupResolver } from '@hookform/resolvers/yup'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatUTCDateTime } from 'common/utils/date'
import { endOfToday } from 'date-fns'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'
import { useGetBreed, useGetBreedBrand, useGetBreedProduct } from 'modules/pond/api/queries'
import { useGetPondNameById, useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { CreateTopupVariables, Topup } from 'modules/shrimpTopup/api/types'
import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import * as Yup from 'yup'

import {
  Grid,
  TextField,
  MenuItem,
  Select,
  InputAdornment,
  FormHelperText,
  FormControl,
  Box,
} from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'

import { normalizeSpeciesName } from 'utils/analytics'

import { useMutateCreateShrimpTopup, useMutateUpdateShrimpTopup } from '../../api/mutation'

interface ShrimpTopupRecordDialogProps {
  onClose: () => void
  open: boolean
  editingTopupId?: string
  initialData?: Topup
}

interface FormData {
  date: Date | null
  time: Date | null
  salinity: string | number
  breedId: any
  breedName: string
  brandId: any
  brandName: string
  productId: any
  breedProductName: string
  shrimpCount: string | number
  averageSize: string | number
}
const ShrimpTopupRecordDialog = ({
  onClose,
  open,
  editingTopupId,
  initialData,
}: ShrimpTopupRecordDialogProps) => {
  const { t } = useTranslation(['shrimpTopup', 'common'])
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId ?? '')
  const invalidateCostQueries = useInvalidateCostQueries()
  const { trackEvent } = useAnalytics()
  const [submitting, setSubmitting] = useState(false)

  const validationSchema = Yup.object().shape({
    date: Yup.date()
      .nullable()
      .required(t('topup.required') + t('topup.startDate')),
    time: Yup.date()
      .nullable()
      .required(t('topup.required') + t('topup.time')),
    salinity: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('topup.required') + t('topup.salinity'))
      .min(0, t('topup.positive_salinity')),
    breedId: Yup.string().required(t('topup.required') + t('topup.breedCondition', { species })),
    brandId: Yup.string().required(t('topup.required') + t('topup.brandName', { species })),
    productId: Yup.string().required(
      t('topup.required') + t('topup.breedProductName', { species })
    ),
    shrimpCount: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('topup.required') + t('topup.ShrimpCount', { species }))
      .positive(t('topup.positive_count', { species })),
    averageSize: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable()
      .notRequired()
      .positive(t('topup.positive_averageSize'))
      .test(
        'required-on-create-for-fish',
        t('topup.required') + t('topup.averageSize'),
        (value) => !isFish || !!editingTopupId || (value !== undefined && value !== null)
      ),
  })

  const {
    control,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    trigger,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      date: new Date(),
      salinity: '',
      breedId: '',
      breedName: '',
      brandId: '',
      brandName: '',
      productId: '',
      breedProductName: '',
      shrimpCount: '',
      averageSize: '',
    },
  })

  const breedId = watch('breedId')
  const brandId = watch('brandId')

  const { data: breeds, isLoading: isBreedLoading } = useGetBreed(pondInfo?.species?.id)
  const { data: breedBrand, isLoading: isBreedBrandLoading } = useGetBreedBrand(
    breedId || undefined
  )
  const { data: breedProduct, isLoading: isBreedProductLoading } = useGetBreedProduct(
    breedId,
    brandId || undefined
  )

  useEffect(() => {
    if (initialData && open) {
      const recordDate = initialData.topupDate ? new Date(initialData.topupDate) : new Date()
      reset({
        date: recordDate,
        time: recordDate,
        salinity: initialData.salinity,
        breedId: '',
        breedName: initialData.breedName,
        brandId: '',
        brandName: initialData.brandName,
        productId: '',
        breedProductName: initialData.breedProductName,
        shrimpCount: initialData.shrimpCount,
        averageSize: initialData.averageSize ?? '',
      })
    } else if (!initialData && open) {
      reset({
        date: new Date(),
        time: new Date(),
        salinity: '',
        breedId: '',
        breedName: '',
        brandId: '',
        brandName: '',
        productId: '',
        breedProductName: '',
        shrimpCount: '',
        averageSize: '',
      })
    }
  }, [initialData, open, reset])

  useEffect(() => {
    if (initialData && breeds && !isBreedLoading && open) {
      const matchedBreed = breeds.find((breed) => breed.name === initialData.breedName)
      if (matchedBreed && matchedBreed.id) {
        setValue('breedId', matchedBreed.id)
        setValue('breedName', matchedBreed.name)
        trigger('breedId')
      }
    }
  }, [initialData, breeds, isBreedLoading, setValue, trigger, open])

  useEffect(() => {
    if (
      initialData &&
      breedBrand &&
      !isBreedBrandLoading &&
      initialData.brandName &&
      open &&
      breedId
    ) {
      const matchedBrand = breedBrand.find((brand) => brand.name === initialData.brandName)
      if (matchedBrand && matchedBrand.id) {
        setValue('brandId', matchedBrand.id)
        setValue('brandName', matchedBrand.name)
        trigger('brandId')
      }
    }
  }, [initialData, breedBrand, isBreedBrandLoading, setValue, trigger, open, breedId])

  useEffect(() => {
    if (
      initialData &&
      breedProduct &&
      !isBreedProductLoading &&
      initialData.breedProductName &&
      open &&
      brandId
    ) {
      const matchedProduct = breedProduct.find(
        (product) => product.productName === initialData.breedProductName
      )
      if (matchedProduct && matchedProduct.id) {
        setValue('productId', matchedProduct.id)
        setValue('breedProductName', matchedProduct.productName)
        trigger('productId')
      }
    }
  }, [initialData, breedProduct, isBreedProductLoading, setValue, trigger, open, brandId])

  useEffect(() => {
    if (breedId && !initialData) {
      setValue('brandId', '')
      setValue('brandName', '')
      setValue('productId', '')
      setValue('breedProductName', '')
      trigger(['brandId', 'productId'])
    }
  }, [breedId, setValue, initialData, trigger])

  useEffect(() => {
    if (brandId && !initialData) {
      setValue('productId', '')
      setValue('breedProductName', '')
      trigger('productId')
    }
  }, [brandId, setValue, initialData, trigger])

  const { mutate: createShrimpTopup, isLoading: isCreating } = useMutateCreateShrimpTopup()
  const { mutate: updateShrimpTopup, isLoading: isUpdating } = useMutateUpdateShrimpTopup()

  const isLoading = isCreating || isUpdating

  useEffect(() => {
    setSubmitting(isLoading)
  }, [isLoading])

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }
    const finalDateTime = new Date(data.date!)
    if (data.time) {
      finalDateTime.setHours(data.time.getHours())
      finalDateTime.setMinutes(data.time.getMinutes())
    }
    const topupData: CreateTopupVariables = {
      topupDate: formatUTCDateTime(finalDateTime.toISOString()),
      salinity: Number(data.salinity),
      breedProductId: Number(data.productId),
      shrimpCount: Number(data.shrimpCount),
    }
    if (isFish) {
      topupData.averageSize =
        data.averageSize === '' || data.averageSize === undefined ? null : Number(data.averageSize)
    }

    const normalizedSpecies = normalizeSpeciesName(species)
    // `source` = the brand (hatchery) id the user picked from the dropdown —
    // never free text. Only the id lives in the request body's scope; the
    // form's `brandId` is the same id, kept here purely for the event param.
    const source = data.brandId ? String(data.brandId) : undefined

    if (editingTopupId) {
      updateShrimpTopup(
        {
          shrimpTopupId: editingTopupId,
          body: topupData,
        },
        {
          onSuccess: () => {
            invalidateCostQueries()
            onClose()
            trackEvent(AnalyticsEvents.stockingRecordUpdated, {
              ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
              ...(source ? { source } : {}),
            })
          },
        }
      )
    } else {
      createShrimpTopup(topupData, {
        onSuccess: () => {
          invalidateCostQueries()
          onClose()
          trackEvent(AnalyticsEvents.stockingRecordCreated, {
            ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
            ...(source ? { source } : {}),
          })
        },
      })
    }
  }

  const isFormLoading =
    initialData && (isBreedLoading || isBreedBrandLoading || isBreedProductLoading)

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="create-shrimp-topup"
      title={labelWithSpecies(
        editingTopupId ? t('topup.edit.title') + t('topup.record') : t('topup.record'),
        species
      )}
      isSubmitting={submitting}
    >
      <form id="create-shrimp-topup" onSubmit={handleFormSubmit(onSubmit)}>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {t('topup.releaseDate')}
          </Grid>
          <Grid item xs={8}>
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <ThaiDatePicker
                      value={field.value}
                      onChange={field.onChange}
                      maxDate={endOfToday()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: errors.date?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box flex={1}>
                <Controller
                  name="time"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      ampm={false}
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.time,
                          helperText: errors.time?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            {t('topup.water')}
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {t('topup.salinity')}
          </Grid>
          <Grid item xs={8}>
            <Controller
              name="salinity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">ppt</InputAdornment>,
                  }}
                  error={!!errors.salinity}
                  helperText={errors.salinity?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            {species}
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {t('topup.breedCondition', { species })}
          </Grid>
          <Grid item xs={8}>
            <FormControl fullWidth error={!!errors.breedId}>
              <Controller
                name="breedId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    id="breed-select"
                    fullWidth
                    displayEmpty
                    error={!!errors.breedId}
                    disabled={isBreedLoading || !breeds?.length}
                    onChange={(e) => {
                      const selectedId = e.target.value
                      field.onChange(selectedId)
                      const selectedBreed = breeds?.find((item) => item.id === selectedId)
                      setValue('breedName', selectedBreed?.name || '')
                      setValue('brandId', '')
                      setValue('brandName', '')
                      setValue('productId', '')
                      setValue('breedProductName', '')

                      trigger('breedId')
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t('topup.select')}
                      {t('topup.breedCondition')}
                    </MenuItem>
                    {breeds?.map((breedItem) => (
                      <MenuItem key={breedItem.id} value={breedItem.id}>
                        {breedItem.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.breedId && <FormHelperText>{errors.breedId.message}</FormHelperText>}
            </FormControl>
          </Grid>

          {breedId && (
            <>
              <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                {t('topup.brandName', { species })}
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth error={!!errors.brandId}>
                  <Controller
                    name="brandId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ''}
                        id="brand-select"
                        fullWidth
                        displayEmpty
                        error={!!errors.brandId}
                        disabled={!breedId || isBreedBrandLoading || !breedBrand?.length}
                        onChange={(e) => {
                          const selectedId = e.target.value
                          field.onChange(selectedId)
                          const selectedBrand = breedBrand?.find((item) => item.id === selectedId)
                          setValue('brandName', selectedBrand?.name || '')
                          setValue('productId', '')
                          setValue('breedProductName', '')

                          trigger('brandId')
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('topup.select')}
                          {t('topup.brandName')}
                        </MenuItem>
                        {breedBrand?.map((brandItem) => (
                          <MenuItem key={brandItem.id} value={brandItem.id}>
                            {brandItem.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.brandId && <FormHelperText>{errors.brandId.message}</FormHelperText>}
                </FormControl>
              </Grid>
            </>
          )}

          {breedId && brandId && (
            <>
              <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                {t('topup.breedProductName', { species })}
              </Grid>
              <Grid item xs={8}>
                <FormControl fullWidth error={!!errors.productId}>
                  <Controller
                    name="productId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value || ''}
                        id="breed-product-select"
                        fullWidth
                        displayEmpty
                        error={!!errors.productId}
                        disabled={!brandId || isBreedProductLoading || !breedProduct?.length}
                        onChange={(e) => {
                          const selectedId = e.target.value
                          field.onChange(selectedId)
                          const selectedProduct = breedProduct?.find(
                            (item) => item.id === selectedId
                          )
                          setValue('breedProductName', selectedProduct?.productName || '')
                          trigger('productId')
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t('topup.select')}
                          {t('topup.breedProductName')}
                        </MenuItem>
                        {breedProduct?.map((productItem) => (
                          <MenuItem key={productItem.id} value={productItem.id}>
                            {productItem.productName}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.productId && <FormHelperText>{errors.productId.message}</FormHelperText>}
                </FormControl>
              </Grid>
            </>
          )}

          {isFish && (
            <>
              <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                {t('topup.averageSize')}
              </Grid>
              <Grid item xs={8}>
                <Controller
                  name="averageSize"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{t('topup.gramPerOne')}</InputAdornment>
                        ),
                      }}
                      error={!!errors.averageSize}
                      helperText={errors.averageSize?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            {t('topup.ShrimpCount', { species })}
          </Grid>
          <Grid item xs={8}>
            <Controller
              name="shrimpCount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {t('topup.additionalReleaseCount')}
                      </InputAdornment>
                    ),
                  }}
                  error={!!errors.shrimpCount}
                  helperText={errors.shrimpCount?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </BaseRecordDialog>
  )
}

export default ShrimpTopupRecordDialog
